import { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { ApiRequestError, apiRequest } from "../api";
import { useAuth } from "../auth";
import { DesktopWorkspace } from "../features/desktop-add-new-data/desktop-workspace";
import {
  addNewDataSchema,
  type AddNewDataFormValues,
} from "../features/desktop-add-new-data/modules/add-new-data/lib/schema";
import type { LocationContext } from "../features/rbim-questionnaire/questionnaire-model";

type RecordResponse = {
  id: string;
  barangayId: string;
  payloadSchemaVersion: number;
  payload: unknown;
};

type EditState = {
  values: AddNewDataFormValues;
  context: LocationContext;
};

export function EditRbimRecordPage() {
  const { user } = useAuth();
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState<EditState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const canEdit = user?.permissions.includes("record.update") === true;

  useEffect(() => {
    if (!user || !canEdit || !id) return;

    let active = true;
    setLoading(true);
    setError("");

    apiRequest<RecordResponse>(`/records/${id}`)
      .then((record) => {
        if (!active) return;
        if (record.payloadSchemaVersion !== 1) {
          throw new Error("This RBIM record uses a questionnaire version that this editor cannot update.");
        }

        const parsed = addNewDataSchema.safeParse(record.payload);
        if (!parsed.success) {
          throw new Error("The stored RBIM questionnaire could not be loaded safely for editing.");
        }

        const household = parsed.data.household;
        const municipalityId = `record:${record.barangayId}`;

        setState({
          values: parsed.data,
          context: {
            municipality: {
              id: municipalityId,
              name: household.municipality || "Assigned Municipality",
              type: "MUNICIPALITY",
              psgcCode: household.municipalityCode || "",
            },
            barangay: {
              id: record.barangayId,
              municipalityId,
              name: household.barangay || "Assigned Barangay",
              psgcCode: household.barangayCode || "",
            },
          },
        });
      })
      .catch((requestError) => {
        if (!active) return;
        setError(
          requestError instanceof ApiRequestError
            ? requestError.message
            : requestError instanceof Error
              ? requestError.message
              : "The RBIM record could not be opened for editing.",
        );
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [canEdit, id, user]);

  if (!user) return null;
  if (!canEdit) return <Navigate to="/access-denied" replace />;
  if (loading) return <main className="centered-state">Loading the RBIM questionnaire…</main>;
  if (error || !state) {
    return <section className="portal-page"><div className="form-error" role="alert">{error || "The RBIM record could not be loaded."}</div></section>;
  }

  return <section className="portal-page">
    <header className="page-heading">
      <div>
        <span className="eyebrow">RBIM household correction</span>
        <h1>Edit Household</h1>
        <p>Edit the complete household record using the same RBIM questionnaire workflow, including household information, family details, and individual member data.</p>
      </div>
    </header>
    <DesktopWorkspace
      context={state.context}
      barangayId={state.context.barangay.id}
      barangays={[state.context.barangay]}
      encoderName={user.displayName}
      draftOwnerId={`${user.id}:edit:${id}`}
      assignedLocationLabel={user.locationLabel}
      geographyLoading={false}
      geographyError=""
      recordUpdatePath={`/records/${id}`}
      initialValues={state.values}
      preserveInitialDateEncoded
      onRecordSaved={() => navigate("/households", { replace: true })}
      onBarangayChange={() => {}}
    />
  </section>;
}
